import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  useColorModeValue,
  Icon,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { 
  FaRoad, 
  FaWater, 
  FaBolt, 
  FaWifi, 
  FaBuilding, 
  FaRecycle,
  FaChartBar,
  FaClipboardCheck,
  FaCogs
} from 'react-icons/fa';

const ServiceCard = ({ icon, title, description, features }) => {
  const bg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  return (
    <Box
      maxW={'320px'}
      w={'full'}
      bg={bg}
      boxShadow={'xl'}
      rounded={'md'}
      p={6}
      textAlign={'center'}
      border={'1px'}
      borderColor={borderColor}
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
    >
      <Box
        display={'inline-flex'}
        alignItems={'center'}
        justifyContent={'center'}
        w={16}
        h={16}
        mb={4}
        color={'white'}
        bg={'brand.500'}
        rounded={'full'}
      >
        <Icon as={icon} w={8} h={8} />
      </Box>
      <Heading fontSize={'xl'} fontFamily={'body'} mb={2}>
        {title}
      </Heading>
      <Text color={'gray.500'} mb={4}>
        {description}
      </Text>
      <VStack align="start" spacing={2}>
        {features.map((feature, index) => (
          <HStack key={index} align="start">
            <Icon as={FaChartBar} color="brand.500" boxSize={3} mt={1} />
            <Text fontSize="sm" color={'gray.600'}>{feature}</Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};

export default function ServicesSection() {
  const services = [
    {
      icon: FaRoad,
      title: 'Transportation Infrastructure',
      description: 'Modern transportation solutions for connected communities',
      features: [
        'Highway & Bridge Design',
        'Smart Traffic Management',
        'Public Transit Planning',
        'Sustainable Mobility Solutions'
      ]
    },
    {
      icon: FaWater,
      title: 'Water & Wastewater',
      description: 'Comprehensive water management and treatment systems',
      features: [
        'Water Treatment Plants',
        'Distribution Networks',
        'Stormwater Management',
        'Wastewater Recycling'
      ]
    },
    {
      icon: FaBolt,
      title: 'Energy Systems',
      description: 'Sustainable energy infrastructure for the future',
      features: [
        'Renewable Energy Integration',
        'Smart Grid Solutions',
        'Energy Storage Systems',
        'Efficiency Optimization'
      ]
    },
    {
      icon: FaWifi,
      title: 'Digital Infrastructure',
      description: 'Building the backbone of smart cities',
      features: [
        'Fiber Optic Networks',
        '5G Infrastructure',
        'IoT Integration',
        'Data Center Planning'
      ]
    },
    {
      icon: FaBuilding,
      title: 'Urban Development',
      description: 'Creating sustainable and livable urban spaces',
      features: [
        'Master Planning',
        'Mixed-Use Development',
        'Green Building Design',
        'Community Facilities'
      ]
    },
    {
      icon: FaRecycle,
      title: 'Environmental Solutions',
      description: 'Protecting our planet through sustainable infrastructure',
      features: [
        'Environmental Impact Assessment',
        'Remediation Services',
        'Carbon Neutral Design',
        'Ecosystem Preservation'
      ]
    }
  ];

  return (
    <Box bg={useColorModeValue('gray.50', 'gray.900')} id="services" py={16}>
      <Container maxW={'7xl'}>
        <Stack spacing={4} as={Container} maxW={'3xl'} textAlign={'center'}>
          <Heading fontSize={'3xl'}>What We Do</Heading>
          <Text color={'gray.600'} fontSize={'xl'}>
            Comprehensive infrastructure solutions tailored to meet the unique challenges 
            of modern development
          </Text>
        </Stack>

        <Container maxW={'7xl'} mt={10}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </SimpleGrid>
        </Container>

        <Divider mt={16} mb={10} />

        <Stack spacing={4} textAlign={'center'} mt={10}>
          <Heading fontSize={'2xl'}>Our Process</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mt={8}>
            <VStack>
              <Icon as={FaClipboardCheck} boxSize={10} color="brand.500" />
              <Text fontWeight="bold">1. Assessment & Planning</Text>
              <Text color={'gray.600'} fontSize={'sm'}>
                Comprehensive analysis of project requirements and site conditions
              </Text>
            </VStack>
            <VStack>
              <Icon as={FaCogs} boxSize={10} color="brand.500" />
              <Text fontWeight="bold">2. Design & Engineering</Text>
              <Text color={'gray.600'} fontSize={'sm'}>
                Innovative design solutions using cutting-edge technology
              </Text>
            </VStack>
            <VStack>
              <Icon as={FaChartBar} boxSize={10} color="brand.500" />
              <Text fontWeight="bold">3. Implementation & Monitoring</Text>
              <Text color={'gray.600'} fontSize={'sm'}>
                Expert execution with continuous quality control and optimization
              </Text>
            </VStack>
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}